import { Experiments } from '@wix/yoshi-flow-editor';
import { PaymentOption, PaymentType } from '../../../types/types';
import { SlotService, SlotServices } from '../../state/types';
import { mapToArray } from '../..';
import { ExperimentsConsts } from '../../../consts/experiments';
import { findSelectedMembership } from '../../multiParticipantFlow/participantPayment';
import { sumSelectedAddonPrices } from '../deposit/percentDeposit';
import {
  bookingRequiresStoredPaymentMethod,
  buildPurchasablePlanVariantKey,
} from '../paymentOptions/utils';

// TODO(Plan & Book): both assumptions below are still pending confirmation —
//   - eCom: a single cart can hold purchasable-plan items together with directly-paid
//     items and/or already-purchased-plan (USE_PLAN) items (a "mixed cart").
//   - Pricing Plans: a purchasable plan's additional-step (info-collection) URL can
//     carry more than one service/booking.
// Both are assumed `true` for now. Once each team confirms, flip the relevant flag —
// setting it to `false` re-enables the matching guard, so the appointment falls back to
// the legacy "Buy a plan" flow instead of "Buy plan & Book".
const MIXED_CARTS_SUPPORTED: boolean = true;
const ADDITIONAL_STEP_URL_SUPPORTS_MULTIPLE_SERVICES: boolean = true;

/**
 * The Plan & Book gate. Two conditions, both required:
 *  - the `PlanAndBook` FT is on for this session, and
 *  - the site's Pricing Plans are migrated to eCom (`isPricingPlansMigratedToEcom`, resolved
 *    once per form load in `fetchInitialData` and kept on `FormState`). Plan & Book buys the
 *    plan through the eCom cart, so an unmigrated site can't run the flow at all.
 *
 * Fails closed on a missing/undefined answer — an unresolved migration state must leave the
 * site on the legacy "Buy a plan" redirect, never opt it into Plan & Book.
 */
export const isPlanAndBookEnabled = ({
  experiments,
  isPricingPlansMigratedToEcom,
}: {
  experiments?: Experiments;
  isPricingPlansMigratedToEcom?: boolean;
}): boolean =>
  Boolean(
    experiments?.enabled(ExperimentsConsts.PlanAndBook) &&
      isPricingPlansMigratedToEcom,
  );

/**
 * Whether the whole appointment can go through the in-cart "Buy plan & Book" flow,
 * decided from the *currently selected* payment option of every service (the user can
 * toggle each service between paying directly and paying with a purchasable plan).
 *
 * Mirrors the design doc's multi-service rules: enable Plan & Book only when the selected
 * payment methods support it, otherwise fall back to the legacy "Buy a plan" flow.
 */
export const isPlanAndBookEligible = ({
  slotServices,
  experiments,
  isPricingPlansMigratedToEcom,
}: {
  slotServices: SlotServices;
  experiments: Experiments;
  isPricingPlansMigratedToEcom?: boolean;
}): boolean => {
  if (!isPlanAndBookEnabled({ experiments, isPricingPlansMigratedToEcom })) {
    return false;
  }

  const selectedOptions = mapToArray<SlotService>(slotServices)
    .map((slotService) => slotService?.selectedPaymentOption)
    .filter((option): option is PaymentOption => Boolean(option));

  const buyPlanAndBookOptions = selectedOptions.filter(
    (option) => option.type === PaymentType.BUY_PLAN_AND_BOOK,
  );

  // Not a Plan & Book flow at all unless at least one service is paid by a purchasable plan.
  if (buyPlanAndBookOptions.length === 0) {
    return false;
  }

  // Mixed cart: a purchasable plan selected alongside a directly-paid item (SINGLE_SESSION)
  // or an already-purchased plan (USE_PLAN). eCom's cart can't currently hold these together.
  const hasMixedCartItem = selectedOptions.some(
    (option) =>
      option.type === PaymentType.SINGLE_SESSION ||
      option.type === PaymentType.USE_PLAN,
  );
  if (hasMixedCartItem && !MIXED_CARTS_SUPPORTED) {
    return false;
  }

  // Additional step: a purchasable plan that needs an info-collection step before purchase is
  // bought via the Pricing Plans additional-step page, not the eCom cart.
  const additionalStepPlanIds = new Set(
    buyPlanAndBookOptions
      .filter((option) => Boolean(option.purchasablePlanInfoCollectionUrl))
      .map((option) =>
        buildPurchasablePlanVariantKey(option.purchasablePlanRef, experiments),
      )
      .filter((catalogItemId): catalogItemId is string =>
        Boolean(catalogItemId),
      ),
  );

  // Hard rule (not flag-gated): each additional-step plan must be bought on its own Pricing
  // Plans page, which can only carry that one plan's purchase. So two or more distinct
  // additional-step plans can't be combined.
  if (additionalStepPlanIds.size > 1) {
    return false;
  }

  if (additionalStepPlanIds.size === 1) {
    const [additionalStepPlanId] = Array.from(additionalStepPlanIds);
    // The additional-step page can carry a mix of bookings redeemed by that one plan and
    // bookings paid directly (SINGLE_SESSION) — PP forwards both as line items, only the
    // plan-covered ones carry a membershipPayment. What it can NOT carry is a booking
    // redeemed by a *different* plan (another purchasable plan or an already-owned one).
    const hasItemRedeemedByAnotherPlan = selectedOptions.some((option) => {
      if (option.type === PaymentType.SINGLE_SESSION) {
        return false;
      }
      return (
        option.type !== PaymentType.BUY_PLAN_AND_BOOK ||
        buildPurchasablePlanVariantKey(
          option.purchasablePlanRef,
          experiments,
        ) !== additionalStepPlanId
      );
    });
    if (hasItemRedeemedByAnotherPlan) {
      return false;
    }

    // Multiple bookings carried through the same additional-step plan's URL (whether
    // plan-covered or paid directly) are only possible if the URL supports multiple.
    if (
      selectedOptions.length > 1 &&
      !ADDITIONAL_STEP_URL_SUPPORTS_MULTIPLE_SERVICES
    ) {
      return false;
    }
  }

  return true;
};

export const getSelectedServicesForBuyPlan = (
  slotServices: SlotServices,
): SlotService[] => {
  return mapToArray<SlotService>(slotServices).filter(
    (slotService) =>
      slotService?.selectedPaymentOption?.type === PaymentType.BUY_PLAN,
  );
};

export const getSelectedServicesForBuyPlanAndBook = (
  slotServices: SlotServices,
): SlotService[] => {
  return mapToArray<SlotService>(slotServices).filter(
    (slotService) =>
      slotService?.selectedPaymentOption?.type ===
      PaymentType.BUY_PLAN_AND_BOOK,
  );
};

/**
 * Whether the buyer chose to pay for at least one service with a purchasable plan.
 * Such a plan can't share the eCom cart with other items (see `isPlanAndBookEligible`)
 * and always submits through the Plan & Book flow regardless of which cta was pressed,
 * so callers use this to drop cart-specific ctas and labels.
 */
export const hasPurchasablePlanSelected = ({
  slotServices,
  experiments,
  isPricingPlansMigratedToEcom,
}: {
  slotServices: SlotServices;
  experiments: Experiments;
  isPricingPlansMigratedToEcom?: boolean;
}): boolean =>
  isPlanAndBookEnabled({ experiments, isPricingPlansMigratedToEcom }) &&
  getSelectedServicesForBuyPlanAndBook(slotServices).length > 0;

/**
 * `bookingRequiresStoredPaymentMethod` over a whole booking's slot services. Every caller
 * must ask about *all* of them — a subset would silently restore the per-service semantics
 * this replaced — so take the booking's `SlotServices` and do the mapping here.
 */
export const slotServicesRequireStoredPaymentMethod = (
  slotServices: SlotServices,
): boolean =>
  bookingRequiresStoredPaymentMethod(
    mapToArray<SlotService>(slotServices).map(({ service }) => service),
  );

export const nonPlanPaidSlotServicesRequireStoredPaymentMethod = (
  slotServices: SlotServices,
): boolean =>
  mapToArray<SlotService>(slotServices).some(
    (slotService) =>
      bookingRequiresStoredPaymentMethod([slotService.service]) &&
      (!slotService.service.planBookingsSkipCardForFee ||
        Array.from({ length: slotService.participants?.length || 1 }).some(
          (_, participantIndex) =>
            !findSelectedMembership(slotService, participantIndex) ||
            sumSelectedAddonPrices(
              slotService.participants?.[participantIndex]?.selectedAddons ??
                slotService.selectedAddons,
            ) > 0,
        )),
  );

/**
 * Whether Plan & Book must be withheld from this booking, so a purchasable plan is offered as
 * the plain "Buy a plan" option instead.
 *
 * Two reasons, both booking-level — which is why this is one predicate rather than a flag each
 * call site ors together. Every site that computes `isPlanAndBookBlocked` must use this, or the
 * rendered dropdown and the default selection can disagree about which option exists.
 *
 * 1. A cancellation-fee / save-card policy needs a payment method captured (ECP-12919).
 * 2. More than one participant: Plan & Book buys the plan and creates the booking in one
 *    transaction, through a flow that creates a single booking and knows nothing about
 *    `participants` — picking it would book the booker alone and silently drop everyone else.
 *    Plain "Buy a plan" is safe here because it creates no booking at all: it sends the
 *    customer to buy the plan, and they come back to the form afterwards. Using a plan the
 *    booker already owns is unaffected.
 */
export const isPlanAndBookBlockedForBooking = (
  slotServices: SlotServices,
): boolean =>
  slotServicesRequireStoredPaymentMethod(slotServices) ||
  mapToArray<SlotService>(slotServices).some(
    ({ participants }) => (participants?.length ?? 0) > 1,
  );

// ECP-12919: a policy-blocked service under Plan & Book selects BUY_PLAN options built from
// `purchasableBenefits`, not the legacy `eligiblePlans` (which isn't fetched once the FT is
// on) — a service backed by either has a real plan behind its selection. Neither is a given:
// the reserved generic "Buy a plan" entry is offered whenever a service is sold as a plan,
// even when no plan resolves.
const hasAvailablePlans = ({
  eligiblePlans,
  purchasableBenefits,
}: SlotService): boolean =>
  Boolean(eligiblePlans?.length || purchasableBenefits?.length);

/**
 * Approximates `isPlanAndBookEligible` for the FT-off (control) arm, so A/B exposure can be
 * reported from both arms. Needed because `purchasableBenefits` — and with it every
 * `BUY_PLAN_AND_BOOK` option `isPlanAndBookEligible` reads — is only fetched once the FT is
 * on (see `fetchInitialData`), so this arm has to infer the same answer from the legacy
 * `BUY_PLAN` selections instead.
 *
 * A booking can hold several different plans — eCom's cart supports that, and the FT-on path
 * only restricts plans needing an info-collection step ("additional step"). So the rules
 * left to mirror are the booking-level policy block and "some service is paid by a plan
 * that actually exists".
 *
 * That last part has to be checked, not assumed from the `BUY_PLAN` selection: with the FT
 * off a pricing-plan-only service still gets the reserved generic "Buy a plan" entry
 * (`getPaymentOptions`) even when no plan resolves, and `getDefaultPaymentOptionId` can pick
 * it as the default — so exposure would otherwise be reported for a booking whose buy-plan
 * cta `ButtonsContainer` disables outright. The treatment arm has no `purchasableBenefits`
 * there, builds no `BUY_PLAN_AND_BOOK` option, and shows no button.
 *
 * One divergence remains: an additional-step plan combined with another plan or with an
 * already-owned membership is excluded there but invisible here, since neither
 * `purchasablePlanInfoCollectionUrl` nor the plan behind a `USE_PLAN` selection can be
 * resolved without the purchasable-plan data this arm never fetches, so such a booking is
 * over-reported. Reviewed with data analysis and accepted as negligible for this A/B — if a
 * later test needs it exact, the control arm would have to fetch `listPurchasablePlans` too
 * (it needs no member context). The arms also read different plan catalogs
 * (`listPlansByItemIdentifier` here vs `listPurchasablePlans` there, the latter skipped for
 * site owners), so a service offered by one and not the other diverges regardless.
 */
const wouldShowBuyPlanAndBookIfFtWereOn = (
  slotServices: SlotServices,
): boolean =>
  !slotServicesRequireStoredPaymentMethod(slotServices) &&
  // One plan-backed service is enough: the FT-on path needs only one
  // BUY_PLAN_AND_BOOK selection to label the cta.
  getSelectedServicesForBuyPlan(slotServices).some(hasAvailablePlans);

/**
 * Whether "Buy Plan & Book" would be shown to the buyer if the `PlanAndBook` FT were
 * enabled for this session — used to report A/B exposure regardless of the FT's real state.
 * Equivalent to `isPlanAndBookEligible` when the FT is actually on (a policy-blocked booking
 * never gets a `BUY_PLAN_AND_BOOK` option built, so that check covers the policy rule too);
 * see `wouldShowBuyPlanAndBookIfFtWereOn` for the FT-off approximation.
 *
 * Not the whole exposure rule: site-owner sessions are excluded as well, in
 * `createOnBuyPlanAndBookWouldShowAction`, since `isOwner()` only exists on the controller's
 * `wixSdkAdapter`. A new caller wanting "would the button show" needs that check too.
 */
export const wouldShowBuyPlanAndBook = ({
  slotServices,
  experiments,
  isPricingPlansMigratedToEcom,
}: {
  slotServices: SlotServices;
  experiments: Experiments;
  isPricingPlansMigratedToEcom?: boolean;
}): boolean => {
  // A site whose Pricing Plans aren't migrated to eCom can't run Plan & Book in either arm,
  // so it isn't part of this A/B at all. Report no exposure for it rather than counting it
  // into the control arm only, which would skew the comparison.
  if (!isPricingPlansMigratedToEcom) {
    return false;
  }

  return experiments.enabled(ExperimentsConsts.PlanAndBook)
    ? isPlanAndBookEligible({
        slotServices,
        experiments,
        isPricingPlansMigratedToEcom,
      })
    : wouldShowBuyPlanAndBookIfFtWereOn(slotServices);
};

export const hasServicesWithoutAvailablePlans = (slotServices: SlotServices) =>
  getSelectedServicesForBuyPlan(slotServices).some(
    (slotService) => !hasAvailablePlans(slotService),
  );

export const checkDifferentBuyPlans = (slotServices: SlotServices): boolean => {
  const selectedServicesForBuyPlan =
    getSelectedServicesForBuyPlan(slotServices);

  const plans = new Set(
    selectedServicesForBuyPlan.map(
      (slotService) => slotService.selectedPaymentOption.id,
    ),
  );

  return plans.size > 1;
};
